import {
  ICRUDBy,
  ICreateFolderMutation,
  IMoveCollectionItemsMutation,
  IRawPropertyCollectionItem,
  TApiGetCollectionFoldersResponse,
  TApiGetItemsResponse,
  TRawPropertyCollectionItem,
  TDuplicatePropertyCollectionItemMutation,
  IDuplicatePropertyCollectionItemMutation,
  IDuplicatePropertyCollectionFolderMutation,
  IRawPropertyCollection,
  TRawPropertyCollection,
} from "@propely/propely-typings";
import { z } from "zod";
import { propelyApi } from "../../../libs/firebase/api";
import { update } from "../../../libs/firebase/api.methods";
import { doc, getFirestore, updateDoc } from "firebase/firestore";

export type PropertyCollectionEntriesInput = {
  propertyId: string;
  collectionId: string;
  folderId?: string;
};

export const getPropertyCollectionItems = ({ propertyId, collectionId, folderId }: PropertyCollectionEntriesInput) => {
  return (
    folderId
      ? propelyApi().get(`/properties/${propertyId}/collections/${collectionId}/folders/${folderId}/items`)
      : propelyApi().get(`/properties/${propertyId}/collections/${collectionId}/items`)
  ).then((response) => {
    return TApiGetItemsResponse.parse(response.data);
  });
};

export const getPropertyCollectionFolders = ({ propertyId, collectionId, folderId }: PropertyCollectionEntriesInput) => {
  return (
    folderId
      ? propelyApi().get(`/properties/${propertyId}/collections/${collectionId}/folders/${folderId}/folders`)
      : propelyApi().get(`/properties/${propertyId}/collections/${collectionId}/folders`)
  ).then((response) => TApiGetCollectionFoldersResponse.parse(response.data));
};

export type CreatePropertyCollectionFolderInput = Omit<PropertyCollectionEntriesInput, "folderId"> & ICreateFolderMutation;

export const createPropertyCollectionFolder = ({ propertyId, collectionId, name, parentFolderId }: CreatePropertyCollectionFolderInput) => {
  return propelyApi()
    .post(`/properties/${propertyId}/collections/${collectionId}/folders`, {
      name,
      parentFolderId,
    })
    .then((response) => response.data);
};

export type UpdatePropertyCollectionFolderInput = PropertyCollectionEntriesInput & ICreateFolderMutation;

export const updatePropertyCollectionFolder = ({ propertyId, collectionId, folderId, name, parentFolderId }: UpdatePropertyCollectionFolderInput) => {
  return propelyApi()
    .put(`/properties/${propertyId}/collections/${collectionId}/folders/${folderId}`, {
      name,
      parentFolderId,
    })
    .then((response) => response.data);
};

export type DeletePropertyCollectionFolderInput = PropertyCollectionEntriesInput;

export const deletePropertyCollectionFolder = ({ propertyId, collectionId, folderId }: DeletePropertyCollectionFolderInput) => {
  return propelyApi()
    .delete(`/properties/${propertyId}/collections/${collectionId}/folders/${folderId}`)
    .then((response) => response.data);
};

export type MovePropertyCollectionItemInput = PropertyCollectionEntriesInput & IMoveCollectionItemsMutation;

export const movePropertyCollectionItem = ({ propertyId, collectionId, folderId, itemIds }: MovePropertyCollectionItemInput) => {
  return propelyApi()
    .patch(`/properties/${propertyId}/collections/${collectionId}/items/move`, {
      folderId,
      itemIds,
    })
    .then((response) => response.data);
};

export const mergeCollectionItems = ({ propertyId, collectionId, itemIDs }: { propertyId: string; collectionId: string; itemIDs: string[] }) => {
  return propelyApi()
    .patch(`/properties/${propertyId}/collections/${collectionId}/items/merge`, {
      itemIDs,
    })
    .then((response) => response.data);
};

export type DeletePropertyDocumentationCollectionItemInput = {
  itemId: string;
  collectionId: string;
  propertyId: string;
  deletedBy: ICRUDBy;
};

export type ArchivePropertyDocumentationCollectionItemInput = {
  itemId: string;
  collectionId: string;
  propertyId: string;

  archived: boolean;
};

export const deletePropertyDocumentationCollectionItem = ({
  itemId,
  collectionId,
  propertyId,
  deletedBy,
}: DeletePropertyDocumentationCollectionItemInput) => {
  return update(
    `/properties/${propertyId}/collections/${collectionId}/items`,
    z.object({
      deletedBy: z.object({
        userID: z.string(),
        date: z.date(),
      }),
    }),
    itemId,
    { deletedBy }
  );
};

export const updatePropertyCollection = ({
  propertyId,
  collectionId,
  data,
}: {
  propertyId: string;
  collectionId: string;
  data: Partial<IRawPropertyCollection>;
}) => {
  return update(`/properties/${propertyId}/collections`, TRawPropertyCollection, collectionId, data);
};

export const archivePropertyDocumentationCollectionItem = ({
  itemId,
  collectionId,
  propertyId,
  archived,
}: ArchivePropertyDocumentationCollectionItemInput) => {
  return update(
    `/properties/${propertyId}/collections/${collectionId}/items`,
    z.object({
      archived: z.boolean(),
    }),
    itemId,
    { archived }
  );
};

export const duplicatePropertyCollectionItem = ({
  itemId,
  collectionId,
  propertyId,
  data,
}: {
  itemId: string;
  collectionId: string;
  propertyId: string;
  data: IDuplicatePropertyCollectionItemMutation;
}) => {
  return propelyApi()
    .post(`/properties/${propertyId}/collections/${collectionId}/items/${itemId}/duplicate`, data)
    .then((response) => response.data);
};

export const duplicateFolder = ({
  propertyId,
  collectionId,
  folderId,
  data,
}: {
  propertyId: string;
  collectionId: string;
  folderId: string;
  data: IDuplicatePropertyCollectionFolderMutation;
}) => {
  return propelyApi()
    .post(`/properties/${propertyId}/collections/${collectionId}/folders/${folderId}/duplicate`, data)
    .then((response) => response.data);
};

export const updatePropertyCollectionItem = ({
  itemID,
  collectionID,
  propertyID,
  data,
  userID,
}: {
  itemID: string;
  collectionID: string;
  propertyID: string;
  data: Partial<IRawPropertyCollectionItem>;
  userID: string;
}) => {
  return updateDoc(doc(getFirestore(), `properties/${propertyID}/collections/${collectionID}/items/${itemID}`), {
    ...data,
    updatedBy: {
      userID,
      date: new Date(),
    },
  });
};
